// Strukturdaten.js – schema.org-Daten als JSON-LD für Suchmaschinen.
// <JsonLd daten={…} /> rendert einen Block; scripts/prerender.mjs schreibt ihn mit der Seite ins statische HTML.
// <BetriebSchema /> steht in src/App.js auf jeder Seite: Betrieb (LocalBusiness, #betrieb), Inhaber (Person, #inhaber), WebSite.
// Leistungsseiten ergänzen einen Service-Block (leistungSchema), /ueber-mich eine ProfilePage, /kontakt eine ContactPage (webseiteSchema).
// Regel: nur Angaben, die sichtbar auf der jeweiligen Seite stehen (Google-Richtlinie); Stammdaten aus src/data/betrieb.js.
// Die Fußzeile zeigt auf jeder Seite Name, Inhaber, Anschrift, Telefon, Erreichbarkeit, E-Mail und die drei Leistungen, deshalb
// dürfen diese Angaben überall stehen; die Ortsliste (areaServed) nur auf /einsatzgebiet, wo sie sichtbar ist.
// Ein JSON-LD-Block wird nicht als Skript ausgeführt, die Content-Security-Policy (docker/nginx.conf) ist nicht betroffen.
import { useLocation } from "react-router-dom";
import betrieb from "../data/betrieb";

const SITE = betrieb.website;
export const BETRIEB_ID = `${SITE}/#betrieb`;
export const INHABER_ID = `${SITE}/#inhaber`;
export const WEBSITE_ID = `${SITE}/#website`;

// „<“ maskieren, damit ein „</script>“ in einem Text den Block nicht vorzeitig beendet
export function JsonLd({ daten }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(daten).replace(/</g, "\\u003c") }} />;
}

const oeffnungszeiten = {
  "@type": "OpeningHoursSpecification",
  dayOfWeek: betrieb.erreichbarkeit.tage,
  opens: betrieb.erreichbarkeit.von,
  closes: betrieb.erreichbarkeit.bis,
};

// Die drei Leistungsseiten, benannt wie im Menü und in der Fußzeile; dieselbe @id und derselbe Name stehen im Service-Block
// der jeweiligen Seite (eine Entität, kein Duplikat)
export const LEISTUNGEN = [
  { pfad: "/baumpflege", name: "Baumpflege" },
  { pfad: "/baumfaellung", name: "Baumfällung" },
  { pfad: "/mulchen", name: "Landschaftspflege" },
];
const leistungId = (pfad) => `${SITE}${pfad}#service`;

export function BetriebSchema() {
  // Ortsliste nur dort, wo sie auf der Seite steht; das Land nennt die Fußzeile überall
  const { pathname } = useLocation();
  const orte = pathname === "/einsatzgebiet" ? betrieb.einsatzorte.map((name) => ({ "@type": "City", name })) : [];
  const daten = {
    "@context": "https://schema.org",
    "@graph": [
      {
        // Kein passenderer schema.org-Untertyp vorhanden (keiner für Baumpflege, Forst oder Landschaftspflege); die Einordnung
        // leisten hasOfferCatalog, die Service-Blöcke und die Kategorien im Unternehmensprofil.
        "@type": "LocalBusiness",
        "@id": BETRIEB_ID,
        name: betrieb.name,
        legalName: betrieb.rechtsname,
        // so steht der Betrieb derzeit im Google-Unternehmensprofil (ohne Leerzeichen um das &, mit Rechtsnamen)
        alternateName: ["Forst&Flur", `Forst&Flur, ${betrieb.rechtsname}`],
        hasMap: "https://maps.app.goo.gl/PL3RZmrT8izF2vSL6",
        // nur, was die Fußzeile auf jeder Seite zeigt
        description: `Baumpflege, Baumfällung und Landschaftspflege aus ${betrieb.ort} für ganz ${betrieb.region}. Inhaber: ${betrieb.inhaber}.`,
        url: `${SITE}/`,
        telephone: betrieb.telefonE164,
        email: betrieb.email,
        // Dateien ohne Hash in public/ (Bilder aus src/ bekommen beim Build einen Hash)
        logo: { "@type": "ImageObject", url: `${SITE}/logo.png`, width: 1200, height: 691, caption: `Logo ${betrieb.name}` },
        image: {
          "@type": "ImageObject",
          url: `${SITE}/og-image.jpg`,
          width: 1200,
          height: 630,
          caption: "Funkgesteuerte Raupe Moritz FR 75 mit Forstmulcher in dichtem Gehölzaufwuchs",
        },
        address: {
          "@type": "PostalAddress",
          streetAddress: betrieb.strasse,
          addressLocality: betrieb.ort,
          postalCode: betrieb.plz,
          addressRegion: betrieb.region,
          addressCountry: betrieb.land,
        },
        geo: { "@type": "GeoCoordinates", latitude: betrieb.geo.lat, longitude: betrieb.geo.lon },
        areaServed: [...orte, { "@type": "State", name: betrieb.region }],
        // Telefonische Erreichbarkeit (Fußzeile, Kontaktseite) als Öffnungszeiten; identisch im Unternehmensprofil eintragen
        openingHoursSpecification: oeffnungszeiten,
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          telephone: betrieb.telefonE164,
          email: betrieb.email,
          availableLanguage: "de",
          hoursAvailable: oeffnungszeiten,
        },
        founder: { "@id": INHABER_ID },
        sameAs: [betrieb.instagram],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: `Leistungen von ${betrieb.name}`,
          itemListElement: LEISTUNGEN.map((l) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", "@id": leistungId(l.pfad), name: l.name, url: `${SITE}${l.pfad}` },
          })),
        },
      },
      {
        "@type": "Person",
        "@id": INHABER_ID,
        name: betrieb.inhaber,
        jobTitle: "Inhaber",
        worksFor: { "@id": BETRIEB_ID },
        url: `${SITE}/ueber-mich`,
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        url: `${SITE}/`,
        name: betrieb.name,
        inLanguage: "de",
        publisher: { "@id": BETRIEB_ID },
      },
    ],
  };
  return <JsonLd daten={daten} />;
}

// Service-Block einer Leistungsseite. leistungen = Unterleistungen, die auf der Seite genannt werden;
// bild nur angeben, wenn es ohne Hash in public/ liegt und den Inhalt der Seite zeigt.
export function leistungSchema({ pfad, name, serviceType, beschreibung, leistungen, alternateName, bild }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": leistungId(pfad),
    name,
    ...(alternateName ? { alternateName } : {}),
    serviceType,
    url: `${SITE}${pfad}`,
    description: beschreibung,
    ...(bild ? { image: bild } : {}),
    provider: { "@id": BETRIEB_ID },
    areaServed: { "@type": "State", name: betrieb.region },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `Leistungen: ${name}`,
      itemListElement: leistungen.map((n) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: n } })),
    },
  };
}

// Block für eine einzelne Seite (z. B. ProfilePage, ContactPage); weitere Eigenschaften über rest
export function webseiteSchema({ typ, pfad, name, ...rest }) {
  return {
    "@context": "https://schema.org",
    "@type": typ,
    "@id": `${SITE}${pfad}#webpage`,
    url: `${SITE}${pfad}`,
    name,
    inLanguage: "de",
    isPartOf: { "@id": WEBSITE_ID },
    ...rest,
  };
}
